import React, { useState, useEffect, } from 'react';
import { useAuthV2 } from '@/context/auth-v2/AuthContext';
import { useAppTheme } from '../../context/ThemeContext';
import { Card, Button, Form, Input, Select, Alert } from '../../components/ui';
import { createClient } from '../../services/supabase';

interface Product {
  id: string;
  name: string;
  expiry_date: string;
  quantity: number;
  business_id: string;
  business_name: string;
}

const RequestDonations: React.FC = () => {
  const { user } = useAuthV2();
  useAppTheme();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<string>('');
  const [requestQuantity, setRequestQuantity] = useState<number>(1);

  useEffect(() => {
    fetchAvailableProducts();
  }, []);

  const fetchAvailableProducts = async () => {
    try {
      setLoading(true);
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          businesses:business_id (name)
        `)
        .eq('status', 'available')
        .gt('expiry_date', new Date().toISOString())
        .order('expiry_date', { ascending: true });

      if (error) throw error;

      const formattedProducts = data.map((product: any) => ({
        ...product,
        business_name: product.businesses.name,
      }));

      setProducts(formattedProducts);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProduct || !requestQuantity) return;

    try {
      const selectedProductData = products.find(p => p.id === selectedProduct);
      if (!selectedProductData) throw new Error('Selected product not found');

      const supabase = await createClient();
      const { error } = await supabase
        .from('donation_requests')
        .insert({
          charity_id: user?.id,
          business_id: selectedProductData.business_id,
          product_id: selectedProduct,
          quantity: requestQuantity,
          status: 'pending',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      setSuccess('Donation request submitted successfully');
      setSelectedProduct('');
      setRequestQuantity(1);
      
      // Refresh available products
      await fetchAvailableProducts();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="space-y-6">
          <div className="flex items-center">
            <h1 className="text-xl font-medium text-navy-blue">Request Donations</h1>
          </div>
          
          {error && (
            <Alert type="error" className="mb-4">
              {error}
            </Alert>
          )}

          {success && (
            <Alert type="success" className="mb-4">
              {success}
            </Alert>
          )}

          <Card>
        <Form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Select Product
              </label>
              <Select
                value={selectedProduct}
                onChange={(value) => setSelectedProduct(value)}
                required options={[]}              >
                <option value="">Select a product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} - {product.business_name} (Expires: {new Date(product.expiry_date).toLocaleDateString()})
                  </option>
                ))}
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Request Quantity
              </label>
              <Input
                type="number"
                min="1"
                value={requestQuantity}
                onChange={(e) => setRequestQuantity(parseInt(e.target.value))}
                required
              />
            </div>

            <Button
              type="submit"
              color="primary"
              disabled={loading || !selectedProduct || !requestQuantity}
            >
              Submit Request
            </Button>
          </div>
        </Form>
      </Card>

      <Card className="mt-6">
        <h2 className="text-lg font-semibold mb-4">Available Products</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Business
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Available Quantity
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expiry Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product) => (
                <tr key={product.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.business_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {product.quantity}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {new Date(product.expiry_date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
        </div>
      </div>
    </div>
  );
};

export default RequestDonations; 